import './App.scss'

import type { FunctionComponent } from 'react'
import React, { useEffect, useMemo, useState } from 'react'
import { Redirect, Route, Switch, useHistory, useLocation } from 'react-router-dom'
import { wpNewsUrlRegex } from '@which/shared'
import { ApolloError } from '@apollo/client'

import { KindeProvider } from '@kinde-oss/kinde-auth-react'

import { routes } from './routes'
import { isLocal } from './shared'
import { ErrorComponent } from './shared/components/Error/ErrorComponent'
import { CompareTrayContextProvider } from './shared/contexts/CompareTrayContext'
import { paywallUrls } from './shared/paywall-urls'
import { decodeToUserJWT } from './shared/types/UserJWTDecoded'
import { getGraphQLError } from './shared/utils/get-graphql-error'
import { watchDataLayer } from './shared/utils/watch-data-layer'

export const App: FunctionComponent = () => {
  const { pathname } = useLocation()
  const history = useHistory<AppHistory>()
  const shouldRemoveTrailingSlash = !wpNewsUrlRegex.test(pathname)
  const [paywallState] = useState(paywallUrls())

  useEffect(() => {
    watchDataLayer()

    if (window.location.host === 'www.which.co.uk') {
      disableConsole()
    }
  }, [])

  useEffect(
    () =>
      history.listen(({ state }) => {
        if (!state?.updateQueryString && !history?.location?.hash) {
          window.scrollTo(0, 0)
        }
      }),
    [history]
  )

  const content = useMemo(
    () => (
      <CompareTrayContextProvider>
        <Switch key="content">
          {shouldRemoveTrailingSlash && <Redirect from="/:url*(/+)" to={pathname.slice(0, -1)} />}
          {routes.map((props) => (
            <Route key={props.path as string} {...props} />
          ))}
          <Route
            component={() => (
              <ErrorComponent
                error={
                  new ApolloError({
                    graphQLErrors: [getGraphQLError('Page not found', '404')],
                  })
                }
              />
            )}
          />
        </Switch>
      </CompareTrayContextProvider>
    ),
    [pathname, shouldRemoveTrailingSlash]
  )
  const redirectUri =
    typeof window !== 'undefined' && window.location?.origin
      ? `${window.location.origin}/login-redirect`
      : String(paywallState.redirectFeatureUri || '')
  if (
    paywallState &&
    paywallState.kindeClientId &&
    paywallState.kindeDomain &&
    paywallState.redirectUri &&
    paywallState.redirectFeatureUri
  ) {
    return (
      <KindeProvider
        clientId={paywallState.kindeClientId}
        domain={paywallState.kindeDomain}
        logoutUri={paywallState.redirectUri}
        redirectUri={redirectUri}
        useInsecureForRefreshToken={isLocal()}
        forceChildrenRender={true}
        callbacks={{
          onEvent: (event: string, state: any) => {
            if (event === 'tokenRefreshed' && state.success) {
              const kindeAccessToken = state.accessToken
              const domain = isLocal() ? 'localhost' : '.which.co.uk'

              const decoded = decodeToUserJWT(kindeAccessToken)
              const salesforceId = decoded?.user_properties?.salesforce_id?.v || null
              const expireDate = new Date(decoded.exp * 1000).toUTCString()

              document.cookie = `sessionId=${kindeAccessToken};path=/; domain=${domain};expires=${expireDate};SameSite=Lax`
              document.cookie = `blaize_session=${kindeAccessToken};path=/; domain=${domain};expires=${expireDate};SameSite=Lax`
              if (salesforceId) {
                document.cookie = `master_id.analytics_flag=3;path=/; domain=${domain};expires=${expireDate}`
                document.cookie = `master_id=${salesforceId};path=/; domain=${domain};expires=${expireDate}`
              }
            }
          },
        }}
      >
        {content}
      </KindeProvider>
    )
  }
  return content
}

///////// IMPLEMENTATION /////////

type AppHistory = {
  updateQueryString: boolean
}

const disableConsole = () => {
  window.console = {
    ...window.console,
    log: () => '',
    error: () => '',
    warn: () => '',
    info: () => '',
  }
}
